import type { FlightPlayerScoresFragment } from '../__generated__/hooks'

/**
 * Best ball grouping + per-hole contributor resolution.
 *
 * "Contributor" = the team member whose individual score on hole N matches
 * the team's resolved best-ball score on hole N (and stablefordPoints, to
 * disambiguate under Net-strokes ranking where two players might post the
 * same raw score but different net values). Tie-break: first match by
 * player order from the API (stable since the resolver returns flight
 * players in a deterministic order).
 *
 * If `teamScores` isn't populated on the entries (event isn't BESTBALL,
 * or the team has no scores yet on this hole) the contributor for the
 * hole is `null` and the team band reads "Venter på …".
 */
export type FlightEntry = FlightPlayerScoresFragment

export type TeamGroup = {
  teamId: string
  teamName: string
  members: FlightEntry[]
  /** Same across all members (best-ball pts are a team property). */
  teamAccumulatedStableford: number
  /** Team's best-ball score row for the requested hole, or null. */
  bestForHole: {
    score: number | null
    stablefordPoints: number | null
    /** Player id whose individual score matched the team's bestball. */
    contributorId: string | null
  }
}

const sameNumberSafe = (
  a: number | null | undefined,
  b: number | null | undefined
) => a != null && b != null && a === b

/**
 * Group flight entries by team, compute per-team running pts + contributor
 * for the visible hole. Entries with no team fall into an `__individual`
 * bucket (preserves rendering for non-bestball flights).
 */
export const groupByTeam = (
  flight: ReadonlyArray<FlightEntry>,
  holeNumber: number
): TeamGroup[] => {
  const buckets = new Map<string, FlightEntry[]>()
  for (const e of flight) {
    const key = e.team?.id ?? '__individual'
    const arr = buckets.get(key) ?? []
    arr.push(e)
    buckets.set(key, arr)
  }

  const groups: TeamGroup[] = []
  for (const [teamId, members] of buckets.entries()) {
    if (teamId === '__individual') continue
    const sample = members[0]
    const teamName = sample.team?.name ?? 'Uten navn'
    const teamScores = sample.teamScores ?? []
    const teamRow = teamScores.find((s) => s.hole.number === holeNumber)
    const teamAccPts = sample.teamAccumulated?.stablefordPoints ?? 0

    let contributorId: string | null = null
    if (teamRow?.score != null) {
      for (const m of members) {
        const own = (m.scores ?? []).find((s) => s.hole.number === holeNumber)
        if (
          sameNumberSafe(own?.score, teamRow.score) &&
          sameNumberSafe(own?.stablefordPoints, teamRow.stablefordPoints)
        ) {
          contributorId = m.id
          break
        }
      }
      // Fallback: under STABLEFORD, the resolver picks by best pts. If
      // raw scores tie but pts match the team row, the first players-by-
      // stableford-pts match wins. The forward loop above already gives
      // that, but as a last-resort fallback when the score field doesn't
      // align (very unlikely), pick by highest stableford pts.
      if (contributorId == null) {
        const byPts = [...members].sort(
          (a, b) =>
            (b.scores?.find((s) => s.hole.number === holeNumber)
              ?.stablefordPoints ?? -1) -
            (a.scores?.find((s) => s.hole.number === holeNumber)
              ?.stablefordPoints ?? -1)
        )
        contributorId = byPts[0]?.id ?? null
      }
    }

    groups.push({
      teamId,
      teamName,
      members,
      teamAccumulatedStableford: teamAccPts,
      bestForHole: {
        score: teamRow?.score ?? null,
        stablefordPoints: teamRow?.stablefordPoints ?? null,
        contributorId,
      },
    })
  }
  return groups
}

/**
 * Heuristic: are we in a best-ball event? We treat any flight where every
 * entry has a `team` AND any entry has `teamScores`/`teamAccumulated`
 * populated as best-ball. This stays self-contained — the page doesn't
 * need to know the event.mode value to switch layouts.
 */
export const isBestBallFlight = (
  flight: ReadonlyArray<FlightEntry>
): boolean => {
  if (!flight.length) return false
  if (!flight.every((e) => e.team != null)) return false
  return flight.some(
    (e) => (e.teamScores?.length ?? 0) > 0 || e.teamAccumulated != null
  )
}
